import cx from 'classnames';
import { useAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';

import {
  brandAtom,
  modelAtom,
  versionAtom,
} from '../../../../hooks/use-picker-selection';
import {
  allSavedCarsAtom,
  currentSavedCarAtom,
  selectVehicleStepAtom,
  unsavedInformationAtom,
  vehiclePickerOpen,
} from '../../../../state/vehicle';
import FeaturedLink from '../../links/featured';
import BPImage from '../image';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../../providers/app-context';

type FlowTitleProps = {
  vehicleData?: any;
  brandAlias?: any;
  modelImage?: any;
  savedVehicles?: any;
};

const FlowTitleSavedCars: FunctionComponent<FlowTitleProps> = ({
  vehicleData,
  brandAlias,
  modelImage,
  savedVehicles,
}: FlowTitleProps) => {
  const { t } = useTranslation();
  const { tenantConfig, brand } = useAppContext();

  const search = new URLSearchParams();
  search.set('brand', vehicleData?.info?.brand?.slug);

  const [, setVehiclePicker] = useAtom(vehiclePickerOpen);
  const [, setBrandAtom] = useAtom(brandAtom);
  const [, setModelAtom] = useAtom(modelAtom);
  const [, setVersionAtom] = useAtom(versionAtom);
  const [, setCurrentSavedCar] = useAtom(currentSavedCarAtom);
  const [, setSelectedVehicleStep] = useAtom(selectVehicleStepAtom);
  const [unsavedInformation, setUnsavedInformation] = useAtom(
    unsavedInformationAtom
  );
  const [totalNumberOfSavedCars, setTotalNumber] = useState(undefined)

  const openVehiclePicker = () => {
    setBrandAtom(null);
    setModelAtom(null);
    setVersionAtom(null);

    if (
      vehicleData?.info &&
      vehicleData?.info.brand &&
      !vehicleData?.info.model
    ) {
      setSelectedVehicleStep('SELECT_MODEL_UNSAVED');
      setUnsavedInformation(true);
      setBrandAtom(vehicleData?.info.brand);
      setVehiclePicker((prevValue) => !prevValue);
      return;
    }

    if (
      (vehicleData?.info && vehicleData?.info.model) ||
      (savedVehicles && savedVehicles?.length)
    ) {
      setSelectedVehicleStep('SELECT_SAVED_CAR');
      setCurrentSavedCar(vehicleData?.info);
    } else {
      if (tenantConfig.id === 8 && brand) {
        setSelectedVehicleStep('SELECT_MODEL');
      } else {
        setSelectedVehicleStep('SELECT_BRAND');
      }
    }
    setVehiclePicker((prevValue) => !prevValue);
    return;
  };

  useEffect(() => {
    if (!savedVehicles) {
      setTotalNumber(undefined);
    } else {
      setTotalNumber(savedVehicles.length);
    }
  }, [savedVehicles]);

  return (
    <div>
      <FeaturedLink
        variant="blank"
        className="lg:text-xl max-lg:text-base !z-1 flex w-full"
        onClick={openVehiclePicker}
      >
        <section
          className={cx('py-4 px-4 w-full', 'bg-neutral-5 hover:bg-brand-10')}
        >
          <section
            className={cx(
              'container lg:px-4 w-full mx-auto',
              'flex items-center lg:gap-12'
            )}
          >
            <div className="flex w-full justify-between items-center max-lg:gap-4 max-lg:grid grid-cols-2">
              <div
                className={cx(
                  'flex gap-4 justify-start lg:justify-center items-center',
                  'py-1 lg:px-2 lg:p-2',
                  'cursor-pointer'
                )}
              >
                {brandAlias ? (
                  <div className="max-lg:hidden">
                    <BPImage
                      src={`${process.env.STATIC_PREFIX}/brands/logos/${vehicleData?.info?.brand?.alias}.svg`}
                      fallbackSrc={`/svg/icons/car.svg`}
                      alt={vehicleData?.info.brand.name}
                      key={vehicleData?.info.brand.name}
                      width={47}
                      height={47}
                    ></BPImage>
                  </div>
                ) : (
                  <div className="max-lg:hidden">
                    <BPImage
                      src={`/svg/icons/car.svg`}
                      alt={'no car selected'}
                      key={vehicleData?.info.brand.name || ''}
                      width={24}
                      height={24}
                    ></BPImage>
                  </div>
                )}
                <div className="flex flex-col">
                  <span
                    className={cx(
                      'text-lg',
                      vehicleData ? 'max-lg:hidden font-bold' : 'font-normal'
                    )}
                  >
                    {vehicleData
                      ? t('common:saved_cars.your-car')
                      : totalNumberOfSavedCars
                        ? t(
                          totalNumberOfSavedCars === 1
                            ? 'common:saved_cars.one'
                            : 'common:saved_cars.more',
                          {
                            count: totalNumberOfSavedCars,
                          }
                        )
                        : t('common:saved_cars.no-saved-cars')}
                  </span>
                  <span
                    className={cx(
                      'text-base line-clamp-2',
                      vehicleData ? 'font-normal' : 'max-lg:hidden font-bold'
                    )}
                  >
                    {vehicleData
                      ? `${vehicleData?.info?.brand?.name || ''} ${vehicleData?.info?.model?.name || ''
                      } ${vehicleData?.info?.version?.name || ''}`
                      : totalNumberOfSavedCars
                        ? t('common:saved_cars.select-a-car')
                        : t('common:saved_cars.add-a-car')}
                  </span>
                </div>
              </div>

              <div
                className={cx(
                  'relative',
                  'flex',
                  'flex-row',
                  'gap-4 max-lg:justify-end'
                )}
              >
                <div
                  className={cx(
                    'relative',
                    'aspect-video',
                    'shrink-0',
                    'flex',
                    'h-[84px] w-fit'
                  )}
                >
                  {modelImage ? (
                    <BPImage
                      src={modelImage}
                      layout="fill" objectFit="contain"
                      objectPosition="center"
                      fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                      className={cx('bg-cover mix-blend-multiply')}
                      alt={t('common:images_alt.vehicle_alt', {
                        vehicle: `${vehicleData?.info?.brand?.name || ''} ${vehicleData?.info?.model?.name || ''
                          } ${vehicleData?.info?.version?.name || ''}`,
                      })}
                      key={modelImage}
                    ></BPImage>
                  ) : (
                    <BPImage
                      src={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                      layout="fill" objectFit="contain"
                      objectPosition="center"
                      className={cx('bg-cover mix-blend-multiply')}
                      alt={'no_image_available'}
                      key={modelImage}
                    ></BPImage>
                  )}
                </div>
                <BPImage
                  src={`/svg/icons/arrow_right.svg`}
                  width={16}
                  height={16}
                  alt="select model"
                  layout="fixed"
                ></BPImage>
              </div>
            </div>
          </section>
        </section>
      </FeaturedLink>
    </div>
  );
};

export default memo(FlowTitleSavedCars);
